// ==========================================
// ONTOLOGY CORE TYPES
// ==========================================
// This file defines the core ontology structure.
// For field types, see ./field-types.ts

import { Icon } from '@examdojo/core/icon';

import { FieldDefinition, InferModel, InferRequiredModel } from './field-types';

// ==========================================
// ID TYPES (Template Literal Based)
// ==========================================
// These types enforce ID patterns at compile-time without branding.
// IDs are tag-agnostic - representations and procedures have unique IDs
// that do not depend on the taxonomy structure.

/** Unique identifier for a Tag (taxonomy node) - Pattern: tag.{name} */
export type TagId = `tag.${string}`;

/** Unique identifier for a Representation (tag-agnostic) - Pattern: rep.{unique_name} */
export type RepresentationId = `rep.${string}`;

/** Unique identifier for a Procedure (tag-agnostic) - Pattern: proc.{unique_name} */
export type ProcedureId = `proc.${string}`;

/** Unique identifier for a primitive Operation - Pattern: op.{name} */
export type OperationId = `op.${string}`;

// ==========================================
// 1. THE TAXONOMY (Tags)
// ==========================================
// The ONLY hierarchy in the system.
// A Tag with a parent_id forms a tree (e.g. Algebra -> Quadratics).

export enum TopicType {
  Foundational = 'foundational', // Primitives, basic arithmetic
  Structural = 'structural', // Standard Form, Fractions, Surds
  Algebraic = 'algebraic', // Polynomials, Equations, Functions
  Generative = 'generative', // Sequences, Series
  Geometric = 'geometric', // Shapes, Vectors, Transformations
  Analytic = 'analytic', // Calculus, Limits
  Statistical = 'statistical', // Probability, Distributions
}

export interface TopicTypeInfo {
  name: TopicType;
  description: string;
  topics: string;
  example: string;
  color: string;
}

export type VerificationStatus = 'in_progress' | 'verified';

export interface TagVerification {
  reviewer: string;
  status: VerificationStatus;
  date: string; // ISO date string (YYYY-MM-DD)
}

export interface Tag {
  id: TagId;
  name: string;
  parent_id?: TagId;
  topic_type?: TopicType;
  prerequisite_tag_ids?: TagId[];
  description?: string;
  icon?: Icon;
  color?: string;
  verification?: TagVerification[];
}

// ==========================================
// 2. THE STATE (Representation)
// ==========================================

export type RepresentationType =
  | 'symbolic' // LaTeX formulas
  | 'numerical' // Numbers/Tables
  | 'state'; // Badges/Status

export enum RepresentationCategory {
  Value = 'value', // A concrete number (Real, Complex, Fraction)
  Form = 'form', // A specific notation of a value (Standard Form, Surd)
  Expression = 'expression', // An unevaluated algebraic structure (Polynomial)
  Object = 'object', // A geometric or abstract entity (Point, Vector, Matrix)
  Function = 'function', // A mapping from input to output (Linear, Quadratic)
  State = 'state', // A logical classification (Convergent, Increasing)
}

export interface RepresentationCategoryInfo {
  name: RepresentationCategory;
  description: string;
  topics: string;
  types: string[];
  example: string;
  svgPath: string;
}

// Model definition - the mathematical structure of the data
export type ModelDefinition = Record<string, FieldDefinition>;

// Base representation interface (for JSON serialization)
export interface Representation<M extends ModelDefinition = ModelDefinition> {
  id: RepresentationId;
  name: string;
  tag_ids: TagId[];
  description: string;
  category: RepresentationCategory;
  type: RepresentationType;
  base_representation_id?: RepresentationId; // Reference to a primitive/base representation for type reuse
  model: M;
  template: string;
  example_data?: InferModel<M>;
  example?: string; // LaTeX-formatted example for display (e.g., "$42$, $3.14$, $-7.5$")
}

// ==========================================
// 3. THE LOGIC (Procedure)
// ==========================================

export enum ProcedureCategory {
  Transformation = 'transformation', // Same value, different form (Real -> Standard Form)
  Calculation = 'calculation', // Calculate a concrete result (f(3) -> 7)
  Operation = 'operation', // Combine entities using abstract op (Add Fractions)
  Derivation = 'derivation', // Generate a new entity type (Function -> Derivative)
  Classifier = 'classifier', // Classify or compare (Is Convergent?)
}

export interface ProcedureCategoryInfo {
  name: ProcedureCategory;
  description: string;
  topics: string;
  example: string;
  color: string;
}

// Base procedure interface (for JSON serialization)
export interface Procedure {
  id: ProcedureId;
  name: string;
  category: ProcedureCategory;
  tag_ids?: TagId[];
  input_ids: RepresentationId[];
  output_id: RepresentationId;
  formula_latex?: string;
  implementation: string;
  dependencies: OperationId[];
  // Runtime execute function (not serialized, but available at runtime)
  execute?: (...inputs: Array<Record<string, unknown>>) => Record<string, unknown>;
}

// ==========================================
// 4. TYPE-SAFE PROCEDURE DEFINITION
// ==========================================
// This is used during authoring to ensure type safety.
// The compiler will verify that inputs/outputs match.

/**
 * Creates a type-safe procedure definition.
 * The implementation function receives typed inputs and must return typed output.
 */
export interface TypedProcedure<TInputModels extends ModelDefinition[], TOutputModel extends ModelDefinition> {
  id: ProcedureId;
  name: string;
  tag_ids: TagId[];
  input_ids: RepresentationId[];
  output_id: RepresentationId;
  formula_latex?: string;
  dependencies: OperationId[];

  // The actual typed implementation
  execute: (...inputs: InferModelArray<TInputModels>) => InferModel<TOutputModel>;
}

// Helper to infer an array of models
type InferModelArray<T extends ModelDefinition[]> = {
  [K in keyof T]: T[K] extends ModelDefinition ? InferModel<T[K]> : never;
};

// ==========================================
// 5. REPRESENTATION REGISTRY
// ==========================================
// Maps representation IDs to their models for type checking.

export type RepresentationRegistry = Record<string, ModelDefinition>;

// ==========================================
// 6. BUILDER HELPERS
// ==========================================

/**
 * Define a representation with full type inference.
 */
export function defineRepresentation<M extends ModelDefinition>(rep: Representation<M>): Representation<M> {
  return rep;
}

// Helper type to extract input types as a tuple
// Uses InferRequiredModel to ensure procedures can only access required fields
type InputModelsAsTuple<TRegistry extends RepresentationRegistry, TInputIds extends readonly string[]> = {
  [K in keyof TInputIds]: TInputIds[K] extends keyof TRegistry ? InferRequiredModel<TRegistry[TInputIds[K]]> : never;
};

/**
 * Define a procedure with type-safe inputs and outputs.
 * This is the main API for authoring procedures.
 * Supports multiple inputs with different types - each input is passed as a separate argument.
 */
export function defineProcedure<
  TRegistry extends RepresentationRegistry,
  const TInputIds extends ReadonlyArray<keyof TRegistry & string>,
  TOutputId extends keyof TRegistry & string,
>(
  registry: TRegistry,
  config: {
    id: ProcedureId;
    name: string;
    category: ProcedureCategory;
    tag_ids?: TagId[];
    input_ids: TInputIds;
    output_id: TOutputId;
    formula_latex?: string;
    dependencies?: OperationId[];
    execute: (...inputs: InputModelsAsTuple<TRegistry, TInputIds>) => InferModel<TRegistry[TOutputId]>;
  },
): Procedure {
  // Serialize the implementation to a string for storage
  const implementation = config.execute.toString();

  return {
    id: config.id,
    name: config.name,
    category: config.category,
    tag_ids: config.tag_ids,
    input_ids: config.input_ids as unknown as RepresentationId[],
    output_id: config.output_id as RepresentationId,
    formula_latex: config.formula_latex,
    dependencies: config.dependencies ?? [],
    implementation,
    execute: config.execute as (...inputs: Array<Record<string, unknown>>) => Record<string, unknown>,
  };
}

// ==========================================
// 7. ROOT OBJECT
// ==========================================

export interface MasterOntology {
  generated_at: string;
  version: string;
  tags: Tag[];
  representations: Representation[];
  procedures: Procedure[];
}
